import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop'; // npm i react-easy-crop
import { Box, Slider } from '@mui/material';
import { Grid } from '@mui/material';
import { AddAPhoto } from '@mui/icons-material';
import ButtonComponent from '../../../../../components/ButtonComponent';
import WebCamCaptureComponent from '../../../../../components/WebCamComponent/WebCamCaptureComponent';
import { convertBase64ToBlob } from '../../../../../utils/TransformUtils';
import ModalComponent from '../../../../../components/ModalComponent';
import { AppContext } from '../../../../../context/AppContextProvider';
import { FaceEditionContext } from '../../../context/FaceEditionContextProvider';
import { t } from 'i18next';

interface ImageFaceProps {
  disabled?: boolean;
  handleCancelImageEdition?: () => void;
  /**
   * Largura/altura finais do recorte (px). Default: 500.
   */
  cropOutputSize?: number;
}

// Util: desenha a área recortada num canvas e retorna Blob
async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  outputSize = 500,
  mime: string = 'image/jpeg',
): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  // desenha mantendo 1:1
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize,
  );

  return await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob as Blob), mime, 0.92);
  });
}

const ImageFace: React.FC<ImageFaceProps> = ({
  disabled,
  handleCancelImageEdition,
  cropOutputSize = 500,
}) => {
  const [isOpenImageEdit, setIsOpenImageEdit] = useState<boolean>(false);
  const [isOpenCrop, setIsOpenCrop] = useState<boolean>(false);

  const {
    selectedImage,
    setSelectedImage,
    isImageChange,
    setIsImageChange,
    snapshotImage,
    setSnapshotImage,
  } = useContext(FaceEditionContext);

  const { setIsShowLoadingComponent } = useContext(AppContext);

  // estado do recorte
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const selectedImageUrl = useMemo(() => {
    if (!selectedImage) return '';
    return typeof selectedImage !== 'string'
      ? URL.createObjectURL(selectedImage)
      : selectedImage;
  }, [selectedImage]);

  useEffect(() => {
    if (isOpenImageEdit && selectedImage) {
      setSnapshotImage(selectedImage);
      setIsShowLoadingComponent(true);
    }
  }, [isOpenImageEdit]);

  const onCapture = (imageBase64: string | null) => {
    if (imageBase64) {
      const blob: Blob = convertBase64ToBlob(imageBase64);
      setSelectedImage(blob);
      setIsOpenCrop(true);
    }
  };

  const onChangeSelectedImage = (file: File) => {
    const blob = new Blob([file], { type: file.type });
    setSelectedImage(blob);
    setIsOpenCrop(true);
  };

  const handleCancelEdition = () => {
    if (handleCancelImageEdition) handleCancelImageEdition();
    setSelectedImage(snapshotImage);
    setSnapshotImage('');
    setIsImageChange(false);
    setIsOpenImageEdit(false);
    setIsOpenCrop(false);
  };

  const handleSaveEdition = () => {
    setIsImageChange(true);
    setSnapshotImage('');
    setIsOpenImageEdit(false);
    setIsOpenCrop(false);
  };

  const handleClose = () => {
    setSelectedImage(snapshotImage);
    setIsOpenImageEdit(false);
    setIsOpenCrop(false);
  };

  const onCropComplete = (
    _: any,
    areaPixels: { x: number; y: number; width: number; height: number },
  ) => {
    setCroppedAreaPixels(areaPixels);
  };

  const handleApplyCrop = async () => {
    if (!selectedImageUrl || !croppedAreaPixels) return;
    const blob = await getCroppedBlob(
      selectedImageUrl,
      croppedAreaPixels,
      cropOutputSize,
    );
    setSelectedImage(blob);
    setIsOpenCrop(false);
    setIsImageChange(true);
  };

  const handleCancelCrop = () => {
    if (!isImageChange) {
      setSelectedImage(snapshotImage);
    }
    setIsOpenCrop(false);
  }

  return (
    <Box sx={{ mt: 1 }}>
      <ButtonComponent
        fullWidth
        color={selectedImage ? 'warning' : 'primary'}
        disabled={disabled}
        startIcon={<AddAPhoto />}
        onClick={() => setIsOpenImageEdit(true)}
      >
        {selectedImage
          ? t(
              'recognitionPage.modalEditFace.buttonCaptureImage.changeFaceImage',
            )
          : t(
              'recognitionPage.modalEditFace.buttonCaptureImage.captureImageFace',
            )}
      </ButtonComponent>

      {/* Modal principal */}
      <ModalComponent
        isOpen={isOpenImageEdit}
        title="Imagem da Face"
        cardContainerStyle={{ width: 480 }}
        onClose={handleClose}
      >
        {!selectedImage ? (
          <WebCamCaptureComponent
            onCapture={onCapture}
            handleCancelEdition={handleCancelEdition}
            onChangeSelectedImage={onChangeSelectedImage}
          />
        ) : (
          <>
            <img
              onLoad={() => setIsShowLoadingComponent(false)}
              src={selectedImageUrl}
              style={{ width: '100%', maxWidth: 420, maxHeight: '40vh', objectFit: 'contain', borderRadius: 8, display: 'block', margin: '0 auto' }}
            />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <ButtonComponent
                  color="primary"
                  fullWidth
                  onClick={() => setIsOpenCrop(true)}
                >
                  Cancelar
                </ButtonComponent>
              </Grid>
              <Grid item xs={6}>
                <ButtonComponent
                  color="error"
                  fullWidth
                  onClick={() => setSelectedImage('')}
                >
                  {t(
                    'recognitionPage.modalEditFace.buttonCaptureImage.captureAgain',
                  )}
                </ButtonComponent>
              </Grid>
              <Grid item xs={12}>
                <ButtonComponent
                  color="success"
                  fullWidth
                  onClick={handleSaveEdition}
                >
                  Salvar
                </ButtonComponent>
              </Grid>
            </Grid>
          </>
        )}
      </ModalComponent>

      {/* Modal de Recorte */}
      <ModalComponent
        isOpen={isOpenCrop}
        title="Recortar imagem (1:1)"
        cardContainerStyle={{ width: 520 }}
        onClose={(event, reason) => {
         if (reason === 'backdropClick') return;
         handleCancelCrop();
        }}
      >
        {selectedImageUrl ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 480,
              height: '40vh',
              minHeight: 200,
              maxHeight: 480,
              background: '#333',
              borderRadius: 1,
              overflow: 'hidden',
              mx: 'auto',
            }}
          >
            <Cropper
              image={selectedImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              restrictPosition
            />
          </Box>
        ) : null}

        <Box sx={{ px: 2, mt: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 70 }}>Zoom</span>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.01}
              onChange={(_, v) => setZoom(v as number)}
              aria-label="Zoom"
            />
          </div>
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <ButtonComponent
              color="primary"
              fullWidth
              onClick={handleCancelCrop}
            >
              Cancelar
            </ButtonComponent>
          </Grid>
          <Grid item xs={6}>
            <ButtonComponent
              color="success"
              fullWidth
              onClick={handleApplyCrop}
            >
              Aplicar recorte
            </ButtonComponent>
          </Grid>
        </Grid>
      </ModalComponent>
    </Box>
  );
};

export default ImageFace;
